import { instance } from "../instance";
import { PATH } from "../../shared/constants";
import { normalizeOperatorEmail } from "../../utils/operatorEmail";
import { operatorPortalIdTokenHeaders } from "./operatorAdminChat";
import { OperatorRole } from "../../utils/operatorRoles";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";

export interface OperatorAccessRecord {
  _id: string;
  email: string;
  firstName?: string;
  /** Display / full name from profile */
  name?: string;
  phoneNumber?: string;
  role?: string;
  /** Full role set (operator + indexing, etc.). Falls back to [role] when missing. */
  roles?: string[];
  teamIds: string[];
  teamNames?: string[];
  avatarUrl?: string;
  /** True when an avatarKey is stored (even if signed URL is missing). */
  hasAvatar?: boolean;
  addressLine1?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  /** Server-computed: name, phone, photo, and mailing address are all set. */
  profileComplete?: boolean;
  created?: string;
  found?: boolean;
  /** Present on POST /access when a new Cognito user was created */
  cognitoCreated?: boolean;
  inviteEmailSent?: boolean;
  inviteEmailError?: string;
  status?: "active" | "linked";
  /** Previous emails linked under this active account */
  linkedEmails?: string[];
  /** startDate ms of most recent finished assigned game (includes linked emails) */
  lastWorkedAt?: number | null;
  /** Count of assigned games that are not finished yet */
  upcomingEventCount?: number;
}

export interface ResendOperatorInviteResponse {
  inviteEmailSent: boolean;
  inviteEmailError?: string;
  inviteEmailChannel?: "ses" | "cognito";
}

export interface LinkOperatorAccountsResponse {
  success: boolean;
  primaryEmail: string;
  secondaryEmail: string;
  linkedEmails: string[];
  cognitoDisabled?: boolean;
  cognitoDisableError?: string;
  cognitoEnabled?: boolean;
  cognitoEnableError?: string;
  teamIds?: string[];
}

export interface OperatorAccountInfo {
  firstName?: string;
  name: string;
  email: string;
  phoneNumber?: string;
  jobTitle?: string;
  organization?: string;
  notes?: string;
  avatarUrl?: string;
  addressLine1?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  teams: string[];
  previousGames: Array<{
    _id: string;
    team1: string;
    team2: string;
    stadium: string;
    startDate: number;
    finishDate?: number;
  }>;
  upcomingGames: Array<{
    _id: string;
    team1: string;
    team2: string;
    stadium: string;
    startDate: number;
    finishDate?: number;
  }>;
  /** Games where this operator submitted the post-game checklist */
  gameChecklists?: Array<{
    gameId: string;
    team1: string;
    team2: string;
    startDate: number;
    submittedAt: string;
    hadProcessIssues: boolean;
  }>;
  totalGamesOperated: number;
  avgMomentsPerGame: number;
  avgPhotosPerMoment: number;
  /** Previous emails whose history is merged into this profile */
  linkedEmails?: string[];
  primaryEmail?: string;
}

export const getOperatorAccountInfo = async (
  email: string,
): Promise<OperatorAccountInfo> => {
  const e = normalizeOperatorEmail(email);
  const response = await instance.get<OperatorAccountInfo>(
    `${PATH.API}/${PATH.OPERATOR}/access/me/account-info?email=${encodeURIComponent(e)}`,
  );
  return response.data;
};

/** Super-admin: another operator's profile and stats. */
export const getOperatorAccountInfoAdmin = async (
  email: string,
): Promise<OperatorAccountInfo> => {
  const e = normalizeOperatorEmail(email);
  const response = await instance.get<OperatorAccountInfo>(
    `${PATH.API}/${PATH.OPERATOR}/access/admin/account-info?email=${encodeURIComponent(e)}`,
  );
  return response.data;
};

/** Super-admin: save profile fields for another operator (or any operator). */
export const updateOperatorProfileAdmin = async (
  viewerEmail: string,
  payload: {
    targetEmail: string;
    firstName: string;
    name: string;
    phoneNumber: string;
    jobTitle: string;
    organization: string;
    notes: string;
    addressLine1: string;
    addressCity: string;
    addressState: string;
    addressZip: string;
  },
): Promise<void> => {
  try {
    const tokenHeaders = operatorPortalIdTokenHeaders();
    const extraHeaders = "headers" in tokenHeaders ? tokenHeaders.headers : {};
    await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/access/admin/profile`,
      {
        ...payload,
        targetEmail: normalizeOperatorEmail(payload.targetEmail),
      },
      {
        params: { email: normalizeOperatorEmail(viewerEmail) },
        headers: {
          "Content-Type": "application/json",
          ...extraHeaders,
        },
      },
    );
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { error?: string; detail?: string } } };
    const msg = ax.response?.data?.detail || ax.response?.data?.error;
    if (msg) throw new Error(msg);
    throw err;
  }
};

export type OperatorProfileLoginLog = {
  _id: string;
  email: string;
  name: string;
  role: string;
  roles: string[];
  roleKind: string;
  createdAtMs: number;
};

/** Super-admin: portal login history for an operator identity (includes linked emails). */
export const listOperatorProfileLoginLogs = async (
  viewerEmail: string,
  targetEmail: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<{ items: OperatorProfileLoginLog[]; total: number }> => {
  const response = await instance.get<{
    items: OperatorProfileLoginLog[];
    total: number;
  }>(`${PATH.API}/${PATH.OPERATOR}/access/admin/login-logs`, {
    params: {
      email: normalizeOperatorEmail(viewerEmail),
      targetEmail: normalizeOperatorEmail(targetEmail),
      limit: opts.limit ?? 50,
      offset: opts.offset ?? 0,
    },
    ...operatorPortalIdTokenHeaders(),
  });
  return {
    items: response.data?.items || [],
    total: response.data?.total || 0,
  };
};

export interface OperatorUpcomingGameRow {
  _id: string;
  team1?: string;
  team2?: string;
  stadium?: string;
  startDate: number;
  finishDate?: number;
  startTime?: string;
  gameContext?: string;
}

/** Super-admin: upcoming assigned games for an operator (not finished). */
export const getOperatorUpcomingGamesAdmin = async (
  email: string,
): Promise<OperatorUpcomingGameRow[]> => {
  const e = normalizeOperatorEmail(email);
  const response = await instance.get<{ games: OperatorUpcomingGameRow[] }>(
    `${PATH.API}/${PATH.OPERATOR}/access/admin/upcoming-games?email=${encodeURIComponent(e)}`,
  );
  return response.data.games || [];
};

/** All profile fields sent on every save so the API persists the full form state */
export const updateOperatorProfile = async (payload: {
  email: string;
  firstName: string;
  name: string;
  phoneNumber: string;
  jobTitle: string;
  organization: string;
  notes: string;
  addressLine1: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
}): Promise<void> => {
  try {
    await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/access/me/profile`,
      { ...payload, email: normalizeOperatorEmail(payload.email) },
      { headers: { "Content-Type": "application/json" } },
    );
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { error?: string; detail?: string } } };
    const msg = ax.response?.data?.detail || ax.response?.data?.error;
    if (msg) throw new Error(msg);
    throw err;
  }
};

export interface TeamAccessUser {
  _id: string;
  email: string;
  firstName?: string;
  name?: string;
  displayName?: string;
  avatarUrl?: string;
}

export type TeamOperatorPreference = "operatorPreferred" | "snapSquadPreferred";

export interface TeamOperatorRosterMember {
  email: string;
  firstName?: string;
  name?: string;
  phoneNumber?: string;
  role?: string;
  preference: TeamOperatorPreference;
  active: boolean;
  lastWorkedAt: number | null;
  eventCount: number;
  operatorEventCount: number;
  snapSquadEventCount: number;
  accessId?: string;
}

export interface TeamOperatorRosterResponse {
  team: {
    _id: string;
    name: string;
    primaryColor?: string;
    logo?: string;
    stadiumName?: string;
  };
  active: TeamOperatorRosterMember[];
  inactive: TeamOperatorRosterMember[];
}

/**
 * Notify game managers when this operator logs in on a game day (server dedupes per game/day).
 */
export const notifyOperatorGameDayLogin = async (email: string): Promise<void> => {
  const e = normalizeOperatorEmail(email);
  await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/access/me/notify-game-day-login`,
    { email: e },
    operatorPortalIdTokenHeaders(),
  );
};

/** Notify super admins on an operator's first successful portal sign-in (server dedupes). */
export const notifyOperatorFirstLogin = async (email: string): Promise<void> => {
  const e = normalizeOperatorEmail(email);
  await instance.post(
    `${PATH.API}/${PATH.OPERATOR}/access/me/notify-first-login`,
    { email: e },
    operatorPortalIdTokenHeaders(),
  );
};

export const getMyAccess = async (
  email: string,
): Promise<OperatorAccessRecord> => {
  const e = normalizeOperatorEmail(email);
  try {
    const response = await instance.get<OperatorAccessRecord>(
      `${PATH.API}/${PATH.OPERATOR}/access/me?email=${
        encodeURIComponent(e)
      }`,
    );
    return response.data;
  } catch (err) {
    console.error("getMyAccess error", err);
    return { _id: "", email: e, teamIds: [], found: false };
  }
};

export const getOperatorAvatarUploadUrl = async (
  email: string,
  contentType = "image/jpeg",
): Promise<{ url: string; fields: Record<string, string>; s3Key: string }> => {
  const e = normalizeOperatorEmail(email);
  const response = await instance.post<{
    url: string;
    fields: Record<string, string>;
    s3Key: string;
  }>(`${PATH.API}/${PATH.OPERATOR}/access/me/avatar-upload-url`, {
    email: e,
    contentType,
  });
  return response.data;
};

export const updateOperatorAvatar = async (
  email: string,
  s3Key: string,
): Promise<void> => {
  await instance.put(`${PATH.API}/${PATH.OPERATOR}/access/me/avatar`, {
    email: normalizeOperatorEmail(email),
    s3Key,
  });
};

export const listAllAccess = async (): Promise<OperatorAccessRecord[]> => {
  try {
    const response = await instance.get<OperatorAccessRecord[]>(
      `${PATH.API}/${PATH.OPERATOR}/access`,
    );
    return response.data || [];
  } catch (err) {
    console.error("listAllAccess error", err);
    return [];
  }
};

/**
 * Operators visible to this user: super-admins plus same-team access peers
 * (full list for super-admins).
 */
export const listAccessDirectory = async (
  viewerEmail: string,
): Promise<OperatorAccessRecord[]> => {
  const e = normalizeOperatorEmail(viewerEmail);
  try {
    const response = await instance.get<OperatorAccessRecord[]>(
      `${PATH.API}/${PATH.OPERATOR}/access/directory?email=${encodeURIComponent(e)}`,
    );
    return response.data || [];
  } catch (err) {
    console.error("listAccessDirectory error", err);
    return [];
  }
};

export const createAccess = async (
  email: string,
  teamIds: string[],
  roles: OperatorRole | OperatorRole[] = "operator",
  firstName: string,
): Promise<OperatorAccessRecord> => {
  const rolesList = Array.isArray(roles) ? roles : [roles];
  const response = await instance.post<OperatorAccessRecord>(
    `${PATH.API}/${PATH.OPERATOR}/access`,
    {
      email: normalizeOperatorEmail(email),
      teamIds,
      roles: rolesList,
      role: rolesList[0],
      firstName,
    },
  );
  return response.data;
};

export const updateAccess = async (
  id: string,
  payload: { teamIds: string[]; role?: OperatorRole; roles?: OperatorRole[] },
): Promise<OperatorAccessRecord> => {
  const response = await instance.put<OperatorAccessRecord>(
    `${PATH.API}/${PATH.OPERATOR}/access/${id}`,
    payload,
  );
  return response.data;
};

export const deleteAccess = async (id: string): Promise<void> => {
  await instance.delete(
    `${PATH.API}/${PATH.OPERATOR}/access/${id}`,
  );
};

/** Super-admin: new temporary password + invite email for an existing operator Cognito user. */
export const resendOperatorInvite = async (
  accessRecordId: string,
  viewerEmail: string,
): Promise<ResendOperatorInviteResponse> => {
  const e = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post<ResendOperatorInviteResponse>(
    `${PATH.API}/${PATH.OPERATOR}/access/${accessRecordId}/resend-invite`,
    {},
    { params: { email: e } },
  );
  return data;
};

/**
 * Super-admin / owner: link a secondary (old email) account under the primary (new email).
 * History stays on both emails; only the primary remains visible in Manage Access.
 */
export const linkOperatorAccounts = async (
  primaryId: string,
  secondaryId: string,
  viewerEmail: string,
): Promise<LinkOperatorAccountsResponse> => {
  const e = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post<LinkOperatorAccountsResponse>(
    `${PATH.API}/${PATH.OPERATOR}/access/${primaryId}/link`,
    { secondaryId },
    { params: { email: e } },
  );
  return data;
};

/** Super-admin / owner: unlink a secondary account from the primary. */
export const unlinkOperatorAccount = async (
  primaryId: string,
  secondaryEmail: string,
  viewerEmail: string,
): Promise<LinkOperatorAccountsResponse> => {
  const e = normalizeOperatorEmail(viewerEmail);
  const { data } = await instance.post<LinkOperatorAccountsResponse>(
    `${PATH.API}/${PATH.OPERATOR}/access/${primaryId}/unlink`,
    { secondaryEmail: normalizeOperatorEmail(secondaryEmail) },
    { params: { email: e } },
  );
  return data;
};

export const getTeamAccess = async (
  teamId: string,
): Promise<TeamAccessUser[]> => {
  try {
    const response = await instance.get<TeamAccessUser[]>(
      `${PATH.API}/${PATH.OPERATOR}/access/team/${teamId}`,
    );
    return response.data || [];
  } catch (err) {
    console.error("getTeamAccess error", err);
    return [];
  }
};

export const updateTeamAccess = async (
  teamId: string,
  emails: string[],
): Promise<TeamAccessUser[]> => {
  const response = await instance.put<TeamAccessUser[]>(
    `${PATH.API}/${PATH.OPERATOR}/access/team/${teamId}`,
    { emails },
  );
  return response.data || [];
};

export const getTeamOperatorRoster = async (
  teamId: string,
): Promise<TeamOperatorRosterResponse> => {
  const response = await instance.get<TeamOperatorRosterResponse>(
    `${PATH.API}/${PATH.OPERATOR}/access/team/${teamId}/roster`,
  );
  return response.data;
};

export const getActiveTeamRosterEmails = async (
  teamId: string,
  preference?: TeamOperatorPreference | "any",
): Promise<string[]> => {
  const response = await instance.get<{ emails: string[] }>(
    `${PATH.API}/${PATH.OPERATOR}/access/team/${teamId}/roster/active`,
    { params: preference && preference !== "any" ? { preference } : undefined },
  );
  return response.data?.emails || [];
};

export const addTeamOperatorRosterMembers = async (
  teamId: string,
  emails: string[],
  preference?: TeamOperatorPreference,
): Promise<TeamOperatorRosterMember[]> => {
  const response = await instance.post<TeamOperatorRosterMember[]>(
    `${PATH.API}/${PATH.OPERATOR}/access/team/${teamId}/roster`,
    { emails, preference },
  );
  return response.data || [];
};

export const updateTeamOperatorRosterMember = async (
  teamId: string,
  email: string,
  patch: { preference?: TeamOperatorPreference; active?: boolean },
): Promise<TeamOperatorRosterMember> => {
  const response = await instance.put<TeamOperatorRosterMember>(
    `${PATH.API}/${PATH.OPERATOR}/access/team/${teamId}/roster/${encodeURIComponent(email)}`,
    patch,
  );
  return response.data;
};

export const removeTeamOperatorRosterMember = async (
  teamId: string,
  email: string,
): Promise<void> => {
  await instance.delete(
    `${PATH.API}/${PATH.OPERATOR}/access/team/${teamId}/roster/${encodeURIComponent(email)}`,
  );
};

export type OperatorBroadcastChannel = "email" | "sms";

export type OperatorBroadcastRecipient = {
  email: string;
  displayName?: string;
  phoneNumber?: string;
  status: "sent" | "failed" | "skipped";
  error?: string;
};

export type OperatorBroadcastLog = {
  _id: string;
  channel: OperatorBroadcastChannel;
  subject?: string;
  body: string;
  sentByEmail: string;
  recipients: OperatorBroadcastRecipient[];
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  created?: string;
};

export type SendOperatorBroadcastResult = {
  success: boolean;
  logId: string;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  recipients: OperatorBroadcastRecipient[];
};

export const listOperatorBroadcastLogs = async (
  viewerEmail: string,
  limit = 50,
): Promise<OperatorBroadcastLog[]> => {
  try {
    const response = await instance.get<OperatorBroadcastLog[]>(
      `${PATH.API}/${PATH.OPERATOR}/access/broadcast/logs`,
      { params: { email: normalizeOperatorEmail(viewerEmail), limit } },
    );
    return response.data || [];
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const sendOperatorBroadcastRequest = async (
  viewerEmail: string,
  payload: {
    channel: OperatorBroadcastChannel;
    recipientEmails: string[];
    subject?: string;
    body: string;
  },
): Promise<SendOperatorBroadcastResult> => {
  try {
    const response = await instance.post<SendOperatorBroadcastResult>(
      `${PATH.API}/${PATH.OPERATOR}/access/broadcast`,
      {
        viewerEmail: normalizeOperatorEmail(viewerEmail),
        channel: payload.channel,
        recipientEmails: payload.recipientEmails,
        subject: payload.subject,
        body: payload.body,
      },
      { params: { email: normalizeOperatorEmail(viewerEmail) } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};
